import { useMemo, useState } from 'react';
import { Copy, Folder, FolderOpen, Heart } from 'lucide-react';
import { ApiScenarioEntry, Scenario, PlayerRole } from '../types/story';
import { getCategoryLabel } from '../lib/community-scenarios';
import { sortFolderScenarios } from '../lib/scenario-order';
import { buildShareUrl, hasThankd, markThanked, thankScenario } from '../lib/community-api';

interface Props {
  scenarios: Scenario[];
  apiScenarios: ApiScenarioEntry[];
  onSelect: (scenario: Scenario) => void;
  onCreate: () => void;
  onDeleteLocal: (scenarioId: string) => void;
  onEditLocal: (scenario: Scenario) => void;
  onApiScenariosChange: (entries: ApiScenarioEntry[]) => void;
  mode?: 'guided' | 'training';
  /** Hervorgehobener Einstieg über den Ordnern (Grundausbildung für Neulinge). */
  featured?: Scenario;
}

interface FolderEntry {
  id: string;
  label: string;
  scenarios: Scenario[];
}

function getRoleBadge(role: PlayerRole): { label: string; cls: string } {
  if (role.startsWith('gruppenführer')) return { label: 'Gruppenführer', cls: 'bg-red-900/40 text-red-300 border border-red-800' };
  if (role === 'truppführer') return { label: 'Truppführer', cls: 'bg-orange-900/40 text-orange-300 border border-orange-800' };
  if (role === 'truppmann') return { label: 'Truppmann', cls: 'bg-amber-900/40 text-amber-300 border border-amber-800' };
  if (role === 'atemschutzüberwachung') return { label: 'Atemschutz', cls: 'bg-blue-900/40 text-blue-300 border border-blue-800' };
  if (role === 'einsatzleit') return { label: 'Leitstelle', cls: 'bg-purple-900/40 text-purple-300 border border-purple-800' };
  if (role === 'thw_helfer') return { label: 'THW-Helfer', cls: 'bg-sky-900/40 text-sky-300 border border-sky-800' };
  if (role === 'thw_gruppenführer') return { label: 'THW-Gruppenführer', cls: 'bg-sky-900/40 text-sky-300 border border-sky-800' };
  if (role === 'rettungsdienst') return { label: 'Rettungsdienst', cls: 'bg-rose-900/40 text-rose-300 border border-rose-800' };
  if (role === 'wasserrettung') return { label: 'Wasserrettung', cls: 'bg-cyan-900/40 text-cyan-300 border border-cyan-800' };
  if (role === 'katastrophenschutz') return { label: 'Katastrophenschutz', cls: 'bg-amber-900/40 text-amber-300 border border-amber-800' };
  return { label: role, cls: 'bg-[#333] text-[#a3a3a3]' };
}

function getScenarioFolder(scenario: Scenario): string {
  if (scenario.category) return scenario.category;
  if (scenario.community?.category) return scenario.community.category;
  const text = `${scenario.id} ${scenario.title} ${scenario.description}`.toLowerCase();
  if (text.includes('verkehr')) return 'verkehr';
  if (text.includes('wasser')) return 'wasser';
  if (text.includes('thl') || text.includes('hilfe')) return 'thl';
  if (text.includes('funk') || text.includes('alarm')) return 'funk';
  if (text.includes('brand') || text.includes('lage')) return 'brand';
  return 'sonstige';
}

const COMMUNITY_FOLDER_ID = '__community__';

export default function ScenarioList({ scenarios, apiScenarios, onSelect, onCreate, onDeleteLocal, onEditLocal, onApiScenariosChange, mode = 'guided', featured }: Props) {
  const isTraining = mode === 'training';
  const [thankedIds, setThankedIds] = useState<Set<string>>(() => {
    const set = new Set<string>();
    apiScenarios.forEach(e => { if (hasThankd(e.shareId)) set.add(e.shareId); });
    return set;
  });
  const [copyTooltip, setCopyTooltip] = useState<string | null>(null);

  const handleThank = async (e: React.MouseEvent, entry: ApiScenarioEntry) => {
    e.stopPropagation();
    if (thankedIds.has(entry.shareId)) return;
    markThanked(entry.shareId);
    setThankedIds(prev => new Set(prev).add(entry.shareId));
    try {
      const newCount = await thankScenario(entry.shareId);
      onApiScenariosChange(apiScenarios.map(a =>
        a.shareId === entry.shareId ? { ...a, thankCount: newCount } : a
      ));
    } catch { /* ignore */ }
  };

  const copyLink = async (e: React.MouseEvent, shareId: string) => {
    e.stopPropagation();
    await navigator.clipboard.writeText(buildShareUrl(shareId));
    setCopyTooltip(shareId);
    setTimeout(() => setCopyTooltip(null), 2000);
  };

  const folders = useMemo<FolderEntry[]>(() => {
    const grouped = new Map<string, Scenario[]>();
    scenarios.forEach(scenario => {
      const folder = getScenarioFolder(scenario);
      grouped.set(folder, [...(grouped.get(folder) ?? []), scenario]);
    });

    return Array.from(grouped.entries())
      .map(([id, folderScenarios]) => ({
        id,
        label: getCategoryLabel(id),
        scenarios: sortFolderScenarios(folderScenarios),
      }))
      .sort((a, b) => a.label.localeCompare(b.label, 'de'));
  }, [scenarios]);

  const [activeFolderId, setActiveFolderId] = useState<string | null>(null);
  const isCommunityActive = activeFolderId === COMMUNITY_FOLDER_ID;
  const activeFolder = isCommunityActive ? null : (folders.find(folder => folder.id === activeFolderId) ?? folders[0]);

  if (scenarios.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="animate-pulse text-[#a3a3a3]">Szenarien werden geladen...</div>
      </div>
    );
  }

  return (
    <div className="space-y-5 compact:space-y-3">
      <div className="flex flex-wrap items-start justify-between gap-4 compact:gap-2">
        <div className="min-w-0 flex-1 basis-60 compact:basis-auto">
          <h2 className="text-2xl font-bold mb-2 compact:text-lg compact:mb-0">{isTraining ? 'Training' : 'Übungs-Szenarien'}</h2>
          <p className="text-[#a3a3a3] compact:hidden">
            {isTraining
              ? 'Nur der Inhalt der Meldung wird gezeigt – du formulierst und sprichst den Funkspruch selbst und siehst danach den korrekten Funkspruch.'
              : 'Ordner auswählen und Szenario mit Karte oder Pfeil starten. Der Muster-Funkspruch wird angezeigt.'}
          </p>
        </div>
        {!isTraining && (
          <button
            onClick={onCreate}
            className="shrink-0 bg-[#dc2626] hover:bg-[#b91c1c] text-white rounded-lg px-4 py-2 font-semibold compact:hidden"
          >
            Szenario erstellen
          </button>
        )}
      </div>

      {featured && !isTraining && (
        <button
          onClick={() => onSelect(featured)}
          className="w-full flex items-center gap-2 rounded-lg border-2 border-[#dc2626] bg-[#dc2626]/10 hover:bg-[#dc2626]/20 px-4 py-3 compact:px-2 compact:py-1.5 text-left font-semibold compact:text-sm min-w-0"
        >
          <span>Neu bei der Feuerwehr? → {featured.title}</span>
        </button>
      )}

      <div className="grid md:grid-cols-[220px_1fr] gap-4 compact:gap-2 items-start">
        <aside className="bg-[#111] border border-[#333] rounded-lg p-2 compact:p-1">
          <div className="px-2 py-2 text-xs uppercase tracking-wide text-[#777] compact:hidden">Ordner</div>
          <div className="space-y-1 compact:space-y-0 compact:flex compact:flex-wrap compact:gap-1">
            {folders.map(folder => {
              const active = !isCommunityActive && folder.id === activeFolder?.id;
              const Icon = active ? FolderOpen : Folder;
              return (
                <button
                  key={folder.id}
                  onClick={() => setActiveFolderId(folder.id)}
                  className={`w-full compact:w-auto flex items-center justify-between gap-2 rounded-lg px-3 py-2 compact:px-2 compact:py-1 text-left text-sm ${
                    active
                      ? 'bg-[#dc2626] text-white'
                      : 'text-[#d4d4d4] hover:bg-[#262626]'
                  }`}
                >
                  <span className="flex items-center gap-2 min-w-0">
                    <Icon size={16} className="shrink-0" />
                    <span className="truncate">{folder.label}</span>
                  </span>
                  <span className={`text-xs ${active ? 'text-white/80' : 'text-[#777]'}`}>{folder.scenarios.length}</span>
                </button>
              );
            })}
            {apiScenarios.length > 0 && (
              <button
                onClick={() => setActiveFolderId(COMMUNITY_FOLDER_ID)}
                className={`w-full compact:w-auto flex items-center justify-between gap-2 rounded-lg px-3 py-2 compact:px-2 compact:py-1 text-left text-sm ${
                  isCommunityActive
                    ? 'bg-[#dc2626] text-white'
                    : 'text-[#d4d4d4] hover:bg-[#262626]'
                }`}
              >
                <span className="flex items-center gap-2 min-w-0">
                  <Heart size={16} className="shrink-0" />
                  <span className="truncate">Community</span>
                </span>
                <span className={`text-xs ${isCommunityActive ? 'text-white/80' : 'text-[#777]'}`}>{apiScenarios.length}</span>
              </button>
            )}
          </div>
        </aside>

        <section className="space-y-3">
          {isCommunityActive ? (
            <>
              <div>
                <h3 className="text-lg font-semibold">Community</h3>
                <p className="text-xs text-[#777]">{apiScenarios.length} Szenarien von der Community</p>
              </div>
              <div className="grid gap-4 compact:gap-2">
                {apiScenarios.map(entry => {
                  const badge = getRoleBadge(entry.scenario.playerRole);
                  const thanked = thankedIds.has(entry.shareId);
                  return (
                    <div
                      key={entry.shareId}
                      role="button"
                      tabIndex={0}
                      onClick={() => onSelect(entry.scenario)}
                      onKeyDown={event => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          onSelect(entry.scenario);
                        }
                      }}
                      className="bg-[#1a1a1a] border border-[#333] rounded-lg p-4 compact:p-2 hover:border-[#dc2626] hover:bg-[#262626] transition-colors cursor-pointer"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap mb-1">
                            <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${badge.cls}`}>
                              {badge.label}
                            </span>
                            <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-[#333] text-[#d4d4d4] border border-[#444]">
                              {getCategoryLabel(entry.category)}
                            </span>
                          </div>
                          <h3 className="text-lg font-semibold text-[#e5e5e5] compact:text-base compact:leading-tight">{entry.title}</h3>
                          <p className="text-sm text-[#a3a3a3] mt-1 compact:hidden">{entry.description}</p>
                          <div className="mt-2 text-xs text-[#666]">
                            von {entry.authorName}
                          </div>
                          <div className="mt-3 flex flex-wrap gap-2" onClick={e => e.stopPropagation()}>
                            <button
                              onClick={e => handleThank(e, entry)}
                              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm border transition-colors ${
                                thanked
                                  ? 'bg-pink-900/40 text-pink-300 border-pink-800 cursor-default'
                                  : 'bg-[#262626] text-[#a3a3a3] border-[#444] hover:bg-pink-900/30 hover:text-pink-300 hover:border-pink-800'
                              }`}
                            >
                              <Heart size={14} className={thanked ? 'fill-current' : ''} />
                              {entry.thankCount > 0 && <span>{entry.thankCount}</span>}
                              <span>{thanked ? 'Danke gegeben' : 'Danke'}</span>
                            </button>
                            <button
                              onClick={e => copyLink(e, entry.shareId)}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm border bg-[#262626] text-[#a3a3a3] border-[#444] hover:bg-[#333]"
                            >
                              <Copy size={14} />
                              {copyTooltip === entry.shareId ? 'Kopiert!' : 'Link teilen'}
                            </button>
                          </div>
                        </div>
                        <button
                          onClick={event => { event.stopPropagation(); onSelect(entry.scenario); }}
                          className="text-[#dc2626] shrink-0 mt-1 p-2 compact:p-0 rounded-lg hover:bg-[#331f1f]"
                          aria-label={`${entry.title} starten`}
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"></polyline></svg>
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          ) : (
            <>
              <div className="flex items-center justify-between gap-3">
                <div>
                  <h3 className="text-lg font-semibold compact:hidden">{activeFolder?.label}</h3>
                  <p className="text-xs text-[#777] compact:hidden">{activeFolder?.scenarios.length ?? 0} Szenarien in diesem Ordner</p>
                </div>
              </div>

              <div className="grid gap-4 compact:gap-2">
                {(activeFolder?.scenarios ?? []).map((scenario) => {
                  const badge = getRoleBadge(scenario.playerRole);
                  const radioTurns = Object.values(scenario.nodes).reduce(
                    (n, node) => n + node.actions.filter(a => !!a.radioCall).length,
                    0
                  );
                  const folder = getScenarioFolder(scenario);

                  return (
                    <div
                      key={scenario.id}
                      role="button"
                      tabIndex={0}
                      onClick={() => onSelect(scenario)}
                      onKeyDown={event => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          onSelect(scenario);
                        }
                      }}
                      className="bg-[#1a1a1a] border border-[#333] rounded-lg p-4 compact:p-2 hover:border-[#dc2626] hover:bg-[#262626] transition-colors cursor-pointer"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap mb-1">
                            <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${badge.cls}`}>
                              {badge.label}
                            </span>
                            <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-[#333] text-[#d4d4d4] border border-[#444] compact:hidden">
                              {getCategoryLabel(folder)}
                            </span>
                            {scenario.community?.source === 'local' && (
                              <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-emerald-900/40 text-emerald-300 border border-emerald-800">
                                Lokal
                              </span>
                            )}
                            {scenario.community?.source === 'community' && (
                              <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-blue-900/40 text-blue-300 border border-blue-800">
                                Community
                              </span>
                            )}
                            {scenario.community?.source === 'license' && (
                              <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-amber-900/40 text-amber-300 border border-amber-800">
                                Lizenz
                              </span>
                            )}
                          </div>
                          <h3 className="text-lg font-semibold text-[#e5e5e5] compact:text-base compact:leading-tight">{scenario.title}</h3>
                          <p className="text-sm text-[#a3a3a3] mt-1 compact:hidden">{scenario.description}</p>
                          <div className="mt-2 text-xs text-[#666] compact:hidden">
                            {radioTurns} Funk-Runden
                            {scenario.community?.authorName && ` · von ${scenario.community.authorName}`}
                          </div>
                          {scenario.community?.source === 'local' && (
                            <div className="mt-3 flex flex-wrap gap-2">
                              <button
                                onClick={event => { event.stopPropagation(); onEditLocal(scenario); }}
                                className="px-3 py-2 rounded-lg bg-[#262626] border border-[#444] hover:bg-[#333] text-sm"
                              >
                                Bearbeiten
                              </button>
                              <button
                                onClick={event => { event.stopPropagation(); onDeleteLocal(scenario.id); }}
                                className="px-3 py-2 rounded-lg bg-[#261a1a] hover:bg-red-900/30 text-red-300 text-sm"
                              >
                                Löschen
                              </button>
                            </div>
                          )}
                        </div>
                        <button
                          onClick={event => { event.stopPropagation(); onSelect(scenario); }}
                          className="text-[#dc2626] shrink-0 mt-1 p-2 compact:p-0 rounded-lg hover:bg-[#331f1f]"
                          aria-label={`${scenario.title} starten`}
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"></polyline></svg>
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </section>
      </div>

      <div className="mt-8 p-4 bg-[#1a1a1a] border border-[#333] rounded-lg text-xs text-[#a3a3a3] compact:hidden">
        <strong className="block mb-2 text-[#e5e5e5]">Hinweis zum Datenschutz:</strong>
        Alle Audiodaten bleiben lokal auf Ihrem Gerät. Lokale Szenarien bleiben spielbar, bis Sie sie löschen.
      </div>
    </div>
  );
}
